import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { orgMonth } from '@/lib/today';
import * as React from 'react';
import { budgetUsedPercent, endOfMonth, formatDuration, startOfMonth } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { ExportMenu } from '@/components/export-menu';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { Hint } from '@/components/cargofl-ui';
import { downloadCsv } from '@/lib/csv';
import { downloadXlsx } from '@/lib/xlsx';
import { money } from '@/lib/invoice-api';
import { usePeriodShortcuts } from '@/lib/shortcuts';
import {
  useProjectMoney,
  useProjectMoneyDetail,
  type MoneyLine,
  type ProjectMoneyRow,
} from '@/lib/project-money-api';

/**
 * What each project is worth, what it cost, and what is left of its budget.
 *
 * One screen rather than the four the previous system had - project overview,
 * project details, monthly report and the accounting status - because they are the
 * same row asked at different widths. The overview IS the list; opening a row is
 * the detail; the month pager is the monthly report.
 *
 * It exists because none of these numbers had a screen. A budget could be set and
 * enforced on save and never seen; `internalRateMinor` was written on every entry
 * and read by nothing; the one migrated partial-billing row rendered as a badge on
 * an admin page. A number that can be stored and never read implies a guarantee
 * that is not there.
 *
 * HR and above. A supervisor may see what an hour of the work they approve is
 * worth - `canViewRates` - but this is every project in the company, its margin,
 * its cost base and everybody who booked to it. Kimai has that exact leak open as
 * a security issue against its own reporting endpoints.
 */
export function ProjectMoneyPage() {
  const [month, setMonth] = React.useState(() => orgMonth());
  // A whole financial year rather than a month. The same rows, a wider window -
  // which is the difference between "how did we do in September" and "how is this
  // project doing".
  const [wide, setWide] = React.useState(false);
  const [includeHidden, setIncludeHidden] = React.useState(false);
  const [open, setOpen] = React.useState<string | null>(null);

  usePeriodShortcuts({
    previous: () => setMonth(shiftMonth(month, -1)),
    next: () => setMonth(shiftMonth(month, 1)),
    today: () => setMonth(orgMonth()),
  });

  const from = wide ? `${month.slice(0, 4)}-01-01` : startOfMonth(`${month}-01`);
  const to = wide ? `${month.slice(0, 4)}-12-31` : endOfMonth(`${month}-01`);

  const { data, isPending, isError, error } = useProjectMoney(from, to, includeHidden);

  const rows = data?.projects ?? [];
  // Everything is one currency in practice, but `Customer.currency` is per customer
  // and editable - so the footer is labelled with the currency of the rows it
  // actually summed, and says so when they are not all the same.
  const currencies = new Set(rows.map((row) => row.customer.currency));
  const currency = currencies.size === 1 ? [...currencies][0]! : '';

  const columns = [
    { header: 'Customer', width: 24 },
    { header: 'Project', width: 26 },
    { header: 'Hours', width: 10, numeric: true },
    { header: 'Earned', width: 14, numeric: true },
    { header: 'Cost', width: 14, numeric: true },
    { header: 'Margin', width: 14, numeric: true },
    { header: 'Invoiced', width: 14, numeric: true },
    { header: 'Not invoiced', width: 14, numeric: true },
  ];
  // Exported as major units so a spreadsheet can add them up. Divided by 100 on a
  // Number only here, at the very edge, where nothing reads the result back.
  const major = (minor: string) => Number(minor) / 100;
  const exportRows = () =>
    rows.map((row) => [
      row.customer.name,
      row.name,
      Number((row.seconds / 3600).toFixed(2)),
      major(row.earnedMinor),
      major(row.costMinor),
      major(row.marginMinor),
      major(row.invoicedMinor),
      major(row.uninvoicedMinor),
    ]);

  return (
    <PageContainer>
      <PageHeader
        title="Project money"
        description="What each project earned, what it cost, and what is left of its budget."
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" aria-label="Previous month"
          title="Previous month (← or j)" onClick={() => setMonth(shiftMonth(month, -1))}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="min-w-[10rem] text-center text-sm font-semibold">
          {wide ? month.slice(0, 4) : monthLabel(month)}
        </span>
        <Button variant="outline" size="icon" aria-label="Next month"
          title="Next month (→ or k)" onClick={() => setMonth(shiftMonth(month, 1))}>
          <ChevronRight className="size-4" />
        </Button>

        <div className="ml-2 flex rounded-md border p-0.5">
          <Button hint={"Show one month"} size="sm" variant={wide ? 'ghost' : 'default'} className="h-7 px-3 text-xs"
            onClick={() => setWide(false)}>
            Month
          </Button>
          <Button hint={"Show the whole financial year"} size="sm" variant={wide ? 'default' : 'ghost'} className="h-7 px-3 text-xs"
            onClick={() => setWide(true)}>
            Year
          </Button>
        </div>

        {/* Retired projects still spent their budget. Leaving them out silently
            makes this disagree with every other report over the same range. */}
        <Button hint={"Also show retired projects"} size="sm" variant={includeHidden ? 'default' : 'outline'}
          onClick={() => setIncludeHidden((v) => !v)}>
          Include retired
        </Button>

        <div className="flex-1" />

        {rows.length > 0 && (
          <ExportMenu
            onCsv={() =>
              downloadCsv(`project-money-${from}.csv`, [columns.map((c) => c.header), ...exportRows()])
            }
            onXlsx={() =>
              downloadXlsx(`project-money-${from}.xlsx`, 'Projects', columns, exportRows())
            }
          />
        )}
      </div>

      {isError && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">
            {error instanceof Error ? error.message : 'Could not work that out.'}
          </AlertDescription>
        </Alert>
      )}

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && (
        <Card>
          <CardContent className="p-0">
            <p className="border-b px-4 py-2 text-xs leading-relaxed text-muted-foreground">
              Hours, earned, cost and invoiced cover {from} to {to}. A budget is measured
              over its own period instead &mdash; all time for a total budget, the calendar
              month for a monthly one &mdash; because a lifetime budget measured over one
              month would read as barely touched every month forever.
            </p>
            <div className="overflow-x-auto">
              <Table className="min-w-[1100px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 z-10 bg-card">Project</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Earned</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="text-right">Invoiced</TableHead>
                    <TableHead className="text-right">Not invoiced</TableHead>
                    <TableHead className="w-56">Budget</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <ProjectRow
                      key={row.id}
                      row={row}
                      from={from}
                      to={to}
                      open={open === row.id}
                      onToggle={() => setOpen(open === row.id ? null : row.id)}
                    />
                  ))}

                  {rows.length > 0 && (
                    <TableRow className="bg-muted/40">
                      <TableCell className="sticky left-0 z-10 bg-muted/40 font-semibold">
                        Total
                        {currency === '' && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            mixed currencies &mdash; not summed
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="ts-num text-right font-semibold">
                        {formatDuration(data.totals.seconds)}
                      </TableCell>
                      {currency === '' ? (
                        <TableCell colSpan={6} />
                      ) : (
                        <>
                          <TableCell className="ts-num text-right font-semibold">
                            {money(data.totals.earnedMinor, currency)}
                          </TableCell>
                          <TableCell className="ts-num text-right font-semibold">
                            {money(data.totals.costMinor, currency)}
                          </TableCell>
                          <TableCell className="ts-num text-right font-semibold">
                            <Margin minor={data.totals.marginMinor} currency={currency} />
                          </TableCell>
                          <TableCell className="ts-num text-right font-semibold">
                            {money(data.totals.invoicedMinor, currency)}
                          </TableCell>
                          <TableCell className="ts-num text-right font-semibold">
                            {money(data.totals.uninvoicedMinor, currency)}
                          </TableCell>
                          <TableCell />
                        </>
                      )}
                    </TableRow>
                  )}

                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                        No projects in that range.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

function ProjectRow({
  row,
  from,
  to,
  open,
  onToggle,
}: {
  row: ProjectMoneyRow;
  from: string;
  to: string;
  open: boolean;
  onToggle: () => void;
}) {
  const currency = row.customer.currency;

  return (
    <>
      <TableRow className={row.visible ? undefined : 'ts-locked'}>
        <TableCell className="sticky left-0 z-10 bg-card">
          <Hint label={'Open this project'}><button
            type="button"
            onClick={onToggle}
            className="flex items-start gap-2 text-left hover:underline"
          >
            <ChevronDown
              className={`mt-0.5 size-4 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
            />
            <span>
              <span className="font-medium">{row.name}</span>
              {!row.visible && (
                <Badge variant="outline" className="ml-2 font-normal">retired</Badge>
              )}
              <span className="block text-xs text-muted-foreground">{row.customer.name}</span>
            </span>
          </button></Hint>
        </TableCell>
        <TableCell className="ts-num text-right">
          {row.seconds > 0 ? formatDuration(row.seconds) : <span className="opacity-30">—</span>}
        </TableCell>
        <TableCell className="ts-num text-right">{money(row.earnedMinor, currency)}</TableCell>
        <TableCell className="ts-num text-right text-muted-foreground">
          {money(row.costMinor, currency)}
        </TableCell>
        <TableCell className="ts-num text-right">
          <Margin minor={row.marginMinor} currency={currency} />
        </TableCell>
        <TableCell className="ts-num text-right">{money(row.invoicedMinor, currency)}</TableCell>
        <TableCell className="ts-num text-right">
          {money(row.uninvoicedMinor, currency)}
          {row.billedOutsideMinor !== '0' && (
            <span className="block text-xs font-normal text-muted-foreground">
              {money(row.billedOutsideMinor, currency)} billed outside
            </span>
          )}
        </TableCell>
        <TableCell>
          <BudgetCell row={row} />
        </TableCell>
      </TableRow>

      {open && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/20 p-0">
            <Detail id={row.id} from={from} to={to} currency={currency} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Money and time budgets, each with what is left.
 *
 * Both are shown when both are set, because they are independent limits: a project
 * can be inside its money budget and out of its hours, and either alone refuses an
 * entry when overbooking is switched off.
 */
function BudgetCell({ row }: { row: ProjectMoneyRow }) {
  const budget = row.budget;
  if (!budget) return <span className="text-xs text-muted-foreground">no budget</span>;

  const currency = row.customer.currency;
  const moneyPercent =
    budget.budgetMinor === '0'
      ? null
      : budgetUsedPercent(BigInt(budget.budgetMinor), BigInt(budget.spentMinor));
  const timePercent =
    budget.timeBudgetSeconds === 0
      ? null
      : budgetUsedPercent(budget.timeBudgetSeconds, budget.spentSeconds);

  return (
    <div className="space-y-1.5 py-1">
      {moneyPercent !== null && (
        <Bar
          percent={moneyPercent}
          label={`${money(budget.spentMinor, currency)} of ${money(budget.budgetMinor, currency)}`}
        />
      )}
      {timePercent !== null && (
        <Bar
          percent={timePercent}
          label={`${formatDuration(budget.spentSeconds)} of ${formatDuration(budget.timeBudgetSeconds)}`}
        />
      )}
      <p className="text-[11px] text-muted-foreground">
        {budget.type === 'month' ? 'this month' : 'all time'}
      </p>
    </div>
  );
}

/** Capped at 100% wide, but the number keeps counting - 140% is the useful part. */
function Bar({ percent, label }: { percent: number; label: string }) {
  const over = percent > 100;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="text-muted-foreground">{label}</span>
        <span className={`ts-num font-medium ${over ? 'text-destructive' : ''}`}>{percent}%</span>
      </div>
      <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full ${over ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
}

/** Negative is real and is the point of showing it, so it is coloured, not hidden. */
function Margin({ minor, currency }: { minor: string; currency: string }) {
  const negative = minor.startsWith('-');
  if (minor === '0') return <span className="opacity-30">—</span>;
  return <span className={negative ? 'text-destructive' : ''}>{money(minor, currency)}</span>;
}

/**
 * One project opened up: the same seconds counted two ways.
 *
 * By activity answers "what kind of work was this" and by person answers "who did
 * it", so both columns total to the row above - which is how you can see the screen
 * is not lying to you.
 */
function Detail({
  id,
  from,
  to,
  currency,
}: {
  id: string;
  from: string;
  to: string;
  currency: string;
}) {
  const { data, isPending } = useProjectMoneyDetail(id, from, to);

  if (isPending) return <Skeleton className="m-4 h-40" />;
  if (!data) return null;

  return (
    <div className="grid gap-6 p-4 lg:grid-cols-2">
      <Breakdown title="By activity" lines={data.byActivity} currency={currency} />
      <Breakdown title="By person" lines={data.byPerson} currency={currency} />

      {data.invoices.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Invoices covering this work
          </h4>
          <div className="space-y-1">
            {data.invoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center gap-3 text-sm">
                <span className="ts-num font-medium">{invoice.number}</span>
                <Badge variant="outline" className="font-normal">
                  {invoice.status.toLowerCase()}
                </Badge>
                <span className="text-muted-foreground">{invoice.issuedOn}</span>
                <span className="ts-num ml-auto">
                  {money(invoice.grossMinor, invoice.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.billings.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Billed outside the timesheet
          </h4>
          <div className="space-y-1">
            {data.billings.map((billing) => (
              <div key={billing.id} className="flex items-center gap-3 text-sm">
                <span className="ts-num">{billing.billedOn}</span>
                <span className="text-muted-foreground">{billing.comment ?? '—'}</span>
                <span className="ts-num ml-auto">{money(billing.amountMinor, currency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Breakdown({
  title,
  lines,
  currency,
}: {
  title: string;
  lines: MoneyLine[];
  currency: string;
}) {
  if (lines.length === 0) {
    return (
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        <p className="text-sm text-muted-foreground">Nothing recorded in this range.</p>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <Table>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id}>
              <TableCell className="py-1.5">{line.name}</TableCell>
              <TableCell className="ts-num py-1.5 text-right">
                {formatDuration(line.seconds)}
              </TableCell>
              <TableCell className="ts-num py-1.5 text-right">
                {money(line.earnedMinor, currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const monthLabel = (month: string) =>
  new Date(`${month}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

function shiftMonth(month: string, delta: number): string {
  const [year, index] = month.split('-').map(Number);
  return new Date(Date.UTC(year!, index! - 1 + delta, 1)).toISOString().slice(0, 7);
}
